import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/onboarding/brand")({
      component: RouteComponent,
});

const steps = [
      { id: "basic", title: "Basics" },
      { id: "profile", title: "Profile" },
      { id: "persona", title: "Persona" },
      { id: "prompts", title: "Prompts" },
];

function RouteComponent() {
      const location = useLocation();
      const currentPath = location.pathname;

      let currentStepIndex = 0;
      if (currentPath.endsWith("/profile")) currentStepIndex = 1;
      else if (currentPath.endsWith("/persona")) currentStepIndex = 2;
      else if (currentPath.endsWith("/prompts")) currentStepIndex = 3;
      const isDenseStep = currentStepIndex >= 1;

      const getStepContent = (index: number) => {
            switch (index) {
                  case 0:
                        return {
                              title: "Start strong with a polished brand setup.",
                              description:
                                    "Add your brand once and we will prepare a tailored starting point for your profile, personas, and prompts.",
                        };
                  case 1:
                        return {
                              title: "Review your brand profile.",
                              description:
                                    "Here's what we found. Review and refine your brand's core identity. The more accurate this is, the better your AI will perform.",
                        };
                  case 2:
                        return {
                              title: "Who are we talking to?",
                              description:
                                    "Defining your target audience helps us tailor insights and content specifically for them.",
                        };
                  case 3:
                        return {
                              title: "Almost there!",
                              description:
                                    "Let's set up some initial prompts so you can get actionable insights right away.",
                        };
                  default:
                        return {
                              title: "Welcome to Upsearch",
                              description: "Let's get your account set up.",
                        };
            }
      };

      const content = getStepContent(currentStepIndex);

      return (
            <div className="flex min-h-svh w-full lg:h-svh lg:overflow-hidden">
                  {/* Left Panel */}
                  <div className="relative hidden w-1/2 max-h-svh shrink-0 flex-col overflow-hidden bg-brand-ink lg:flex lg:h-svh">
                        {/* Base gradient */}
                        <div className="absolute inset-0 bg-gradient-to-br from-brand-ink via-brand-ink-deep to-brand-ink" />

                        {/* Blurred orbs — marketing site style */}
                        <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-brand-accent/10 blur-[90px]" />
                        <div className="absolute top-1/4 -right-16 h-72 w-72 rounded-full bg-brand-teal/18 blur-[80px]" />
                        <div className="absolute -top-16 left-1/3 h-56 w-56 rounded-full bg-brand-accent/6 blur-[100px]" />

                        <div className="relative z-10 flex flex-col h-full p-10 xl:p-14">
                              {/* Logo */}
                              <div>
                                    <img
                                          src="/brand/logo/upsearch-logo-transparent-bg.png"
                                          alt="Upsearch"
                                          className="h-7"
                                    />
                              </div>

                              {/* Center content */}
                              <div className="flex-1 flex flex-col justify-center gap-8">
                                    {/* Step badge — matches marketing site navy-section badge style */}
                                    <div className="inline-flex w-fit items-center gap-2 bg-white/8 border border-white/10 rounded-full px-3.5 py-1.5">
                                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-accent" />
                                          <span
                                                className="text-[0.6875rem] font-bold uppercase tracking-[0.15em] text-brand-accent"
                                                style={{ fontFamily: "'DM Sans', sans-serif" }}
                                          >
                                                Step {currentStepIndex + 1} of {steps.length}
                                          </span>
                                    </div>

                                    {/* Heading — Outfit Black, tight tracking */}
                                    <h2
                                          className="text-[2.25rem] xl:text-[2.75rem] font-black text-white leading-[1.08] tracking-[-0.03em] transition-all duration-500 max-w-sm"
                                          style={{ fontFamily: "'Outfit', sans-serif" }}
                                    >
                                          {content.title}
                                    </h2>

                                    {/* Description — DM Sans */}
                                    <p
                                          className="text-white/55 text-[0.9375rem] leading-[1.6] transition-all duration-500 max-w-xs"
                                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                                    >
                                          {content.description}
                                    </p>

                                    {/* Feature list */}
                                    <div className="space-y-3.5 pt-1">
                                          {[
                                                "AI scans your site and extracts brand identity",
                                                "Personas built around your target audience",
                                                "Prompts optimised for your brand voice",
                                          ].map((item) => (
                                                <div key={item} className="flex items-center gap-3">
                                                      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-brand-accent/25 bg-brand-accent/15">
                                                            <div className="h-1.5 w-1.5 rounded-full bg-brand-accent" />
                                                      </div>
                                                      <span
                                                            className="text-white/60 text-sm"
                                                            style={{ fontFamily: "'DM Sans', sans-serif" }}
                                                      >
                                                            {item}
                                                      </span>
                                                </div>
                                          ))}
                                    </div>
                              </div>

                              {/* Footer — segmented progress */}
                              <div className="flex items-center gap-1.5">
                                    {steps.map((step, index) => (
                                          <div
                                                key={step.id}
                                                className={cn(
                                                      "h-[3px] rounded-full transition-all duration-500",
                                                      index < currentStepIndex
                                                            ? "flex-1 bg-brand-accent/60"
                                                            : index === currentStepIndex
                                                              ? "flex-[2] bg-brand-accent"
                                                              : "flex-1 bg-white/12",
                                                )}
                                          />
                                    ))}
                              </div>
                        </div>
                  </div>

                  {/* Right Panel */}
                  <div className="flex-1 bg-background lg:min-h-0 lg:overflow-y-auto">
                        <div
                              className={cn(
                                    "mx-auto flex min-h-svh w-full max-w-md flex-col p-6 sm:max-w-lg sm:p-8 lg:min-h-full lg:max-w-xl lg:px-10 xl:max-w-xl",
                                    isDenseStep ? "justify-start lg:py-6" : "justify-center lg:py-12",
                              )}
                        >
                              {/* Stepper */}
                              <div className={cn(isDenseStep ? "mb-6" : "mb-12")}>
                                    <div className="relative">
                                          {/* Track */}
                                          <div className="absolute left-[10%] right-[10%] top-4 h-0.5 bg-brand-ink/8" />
                                          {/* Progress fill */}
                                          <div
                                                className="absolute left-[10%] top-4 h-0.5 bg-brand-accent transition-all duration-500 ease-in-out"
                                                style={{
                                                      width: `calc(${(currentStepIndex / (steps.length - 1)) * 80}%)`,
                                                }}
                                          />

                                          <div className="relative flex justify-between">
                                                {steps.map((step, index) => {
                                                      const isCompleted = index < currentStepIndex;
                                                      const isCurrent = index === currentStepIndex;

                                                      return (
                                                            <div key={step.id} className="flex flex-col items-center gap-3 w-24">
                                                                  <div
                                                                        className={cn(
                                                                              "z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 bg-background transition-all duration-300",
                                                                              isCompleted
                                                                                    ? "border-brand-accent bg-brand-accent text-brand-ink"
                                                                                    : isCurrent
                                                                                      ? "border-brand-ink text-brand-ink"
                                                                                      : "border-brand-ink/15 text-brand-ink/30",
                                                                        )}
                                                                  >
                                                                        {isCompleted ? (
                                                                              <Check className="h-4 w-4" strokeWidth={2.5} />
                                                                        ) : (
                                                                              <span className="text-sm font-semibold">{index + 1}</span>
                                                                        )}
                                                                  </div>
                                                                  <span
                                                                        className={cn(
                                                                              "text-xs font-semibold text-center tracking-wide uppercase transition-colors duration-300",
                                                                              isCurrent
                                                                                    ? "text-brand-ink"
                                                                                    : isCompleted
                                                                                      ? "text-brand-ink/50"
                                                                                      : "text-brand-ink/25",
                                                                        )}
                                                                  >
                                                                        {step.title}
                                                                  </span>
                                                            </div>
                                                      );
                                                })}
                                          </div>
                                    </div>
                              </div>

                              {/* Content */}
                              <div className="flex min-h-0 flex-1 flex-col">
                                    <Outlet />
                              </div>
                        </div>
                  </div>
            </div>
      );
}
